import {
  Button,
  Popover,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState, useMemo } from "react";

const MultiFilter = ({
  anchorEl,
  setAnchorEl,
  columns,
  values,
  onApplyFilter,
}) => {
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedValues, setSelectedValues] = useState([]);

  const customSort = (a, b) => {
    const A = a?.label ?? a;
    const B = b?.label ?? b;

    if (A == null || B == null) return 0;

    const strA = String(A);
    const strB = String(B);

    const getType = (char) => {
      if (/[^a-zA-Z0-9]/.test(char)) return 0; // special char
      if (/[0-9]/.test(char)) return 1; // number
      if (/[A-Z]/.test(char)) return 2; // uppercase
      if (/[a-z]/.test(char)) return 3; // lowercase
      return 4;
    };

    const typeA = getType(strA[0]);
    const typeB = getType(strB[0]);

    // 1️⃣ Sort by type
    if (typeA !== typeB) {
      return typeA - typeB;
    }

    // 2️⃣ Same letters, different case → uppercase first
    if (strA.toLowerCase() === strB.toLowerCase() && strA !== strB) {
      return strA < strB ? -1 : 1;
    }

    // 3️⃣ Normal sort (handles numbers properly)
    return strA.localeCompare(strB, undefined, { numeric: true });
  };

  const COLUMNS = (columns || []).slice().sort(customSort);

  const open = Boolean(anchorEl);

  // Get unique values from selected column
  const getColumnValues = (column) => {
    const rawValues = values.map((r) => r.rowData[column.name]);

    // Normalize values for UI + keep original value
    const normalized = rawValues.map((v) => {
      if (v === null) {
        return { label: "NULL", value: null };
      }
      if (v === undefined) {
        return { label: "UNDEFINED", value: undefined };
      }
      if (Array.isArray(v) || typeof v === "object") {
        return {
          label: JSON.stringify(v),
          value: v,
        };
      }
      return { label: String(v), value: v };
    });

    // Remove duplicates based on actual value
    const uniqueValues = Array.from(
      new Map(
        normalized.map((item) => [
          JSON.stringify(item.value), // use real value for uniqueness
          item,
        ])
      ).values()
    );

    return uniqueValues.sort(customSort);
  };

  const handleApply = () => {
    onApplyFilter(
      selectedColumn,
      selectedValues.map((v) => v.value)
    );
    setAnchorEl(null);
    setSelectedColumn(null);
    setSelectedValues([]);
  };

  const columnValues = useMemo(() => {
    return selectedColumn ? getColumnValues(selectedColumn) : [];
  }, [selectedColumn, values]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <div className="custom-style">
        {/* Column Select */}
        <Autocomplete
          options={COLUMNS}
          value={selectedColumn}
          onChange={(e, newVal) => {
            setSelectedColumn(newVal);
            setSelectedValues([]); // reset on column change
          }}
          sx={{
            fontSize: "14px", // base font size
            "& .MuiInputBase-root": {
              padding: "2px 10px 2px 10px",
              fontSize: "14px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "14px",
            },
            "& .MuiAutocomplete-endAdornment": {
              top: "calc(100% - 18px)", // center the dropdown icon
            },
          }}
          slotProps={{
            popper: {
              sx: {
                "& .MuiAutocomplete-listbox": {
                  fontSize: "14px", // ⬅️ dropdown font size
                  padding: "2px 0",
                },
                "& .MuiAutocomplete-option": {
                  padding: "6px 12px",
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Column" />
          )}
        />

        {/* Value Select */}
        {selectedColumn && (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={columnValues}
            value={selectedValues}
            onChange={(e, newVal) => setSelectedValues(newVal)}
            getOptionLabel={(option) => option?.label ?? ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  sx={{
                    transform: "scale(.9)", // scale up (1 = default size)
                    padding: "2px", // optional: reduce padding
                  }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            sx={{
              fontSize: "14px", // base font size
              "& .MuiInputBase-root": {
                padding: "2px 10px 2px 10px",
                fontSize: "16px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
              },
              "& .MuiAutocomplete-endAdornment": {
                top: "calc(100% - 18px)", // center the dropdown icon
              },
            }}
            slotProps={{
              popper: {
                sx: {
                  "& .MuiAutocomplete-listbox": {
                    fontSize: "14px", // ⬅️ dropdown font size
                    padding: "2px 0",
                  },
                  "& .MuiAutocomplete-option": {
                    padding: "2px 2px",
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Values" />
            )}
          />
        )}

        <Button
          onClick={handleApply}
          variant="contained"
          disabled={
            !selectedColumn || !selectedValues || selectedValues.length === 0
          }
          style={{ marginTop: "10px" }}
        >
          Apply Filter
        </Button>
      </div>
    </Popover>
  );
};

export default MultiFilter;
